import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  orderBy,
  query,
} from "firebase/firestore";
import { db } from "../firebase";
import "./AdminTrainers.css";

function AdminTrainers() {
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const [form, setForm] = useState({
    nameAr: "",
    nameEn: "",
    specialtyAr: "",
    specialtyEn: "",
    experience: "",
    bioAr: "",
    bioEn: "",
    image: "",
  });

  const [preview, setPreview] = useState("");

  useEffect(() => {
    fetchTrainers();
  }, []);

  // =========================================================
  // GET TRAINERS
  // =========================================================

  const fetchTrainers = async () => {
    try {
      const q = query(
        collection(db, "trainers"),
        orderBy("createdAt", "desc")
      );

      const snapshot = await getDocs(q);

      const data = snapshot.docs.map((item) => ({
        id: item.id,
        ...item.data(),
      }));

      setTrainers(data);
    } catch (error) {
      console.error("FETCH TRAINERS ERROR:", error);
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // FORM CHANGE
  // =========================================================

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // =========================================================
  // IMAGE COMPRESSION
  // =========================================================

  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (event) => {
        const img = new Image();

        img.onload = () => {
          const canvas = document.createElement("canvas");

          const maxWidth = 900;
          const maxHeight = 900;

          let width = img.width;
          let height = img.height;

          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }

          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext("2d");

          if (!ctx) {
            reject(new Error("Canvas is not supported"));
            return;
          }

          ctx.drawImage(img, 0, 0, width, height);

          const compressed = canvas.toDataURL(
            "image/jpeg",
            0.72
          );

          resolve(compressed);
        };

        img.onerror = () => {
          reject(new Error("Failed to load image"));
        };

        img.src = event.target.result;
      };

      reader.onerror = () => {
        reject(new Error("Failed to read file"));
      };

      reader.readAsDataURL(file);
    });
  };

  // =========================================================
  // IMAGE SELECT
  // =========================================================

  const handleImage = async (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    // Check image type
    if (!file.type.startsWith("image/")) {
      alert("يرجى اختيار ملف صورة فقط");
      return;
    }

    // Check size - 10MB maximum before compression
    if (file.size > 10 * 1024 * 1024) {
      alert("حجم الصورة كبير جدًا. اختاري صورة أقل من 10MB.");
      return;
    }

    try {
      const image = await compressImage(file);

      setPreview(image);

      setForm((prev) => ({
        ...prev,
        image,
      }));
    } catch (error) {
      console.error("IMAGE ERROR:", error);
      alert("حدث خطأ أثناء معالجة الصورة");
    }
  };

  // =========================================================
  // REMOVE IMAGE
  // =========================================================

  const removeImage = () => {
    setPreview("");

    setForm((prev) => ({
      ...prev,
      image: "",
    }));
  };

  // =========================================================
  // RESET FORM
  // =========================================================

  const resetForm = () => {
    setForm({
      nameAr: "",
      nameEn: "",
      specialtyAr: "",
      specialtyEn: "",
      experience: "",
      bioAr: "",
      bioEn: "",
      image: "",
    });

    setPreview("");
  };

  // =========================================================
  // ADD TRAINER
  // =========================================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !form.nameAr.trim() ||
      !form.nameEn.trim() ||
      !form.specialtyAr.trim() ||
      !form.specialtyEn.trim()
    ) {
      alert(
        "يرجى تعبئة الاسم والاختصاص بالعربي والإنكليزي"
      );
      return;
    }

    try {
      setSaving(true);

      await addDoc(collection(db, "trainers"), {
        nameAr: form.nameAr.trim(),
        nameEn: form.nameEn.trim(),

        specialtyAr: form.specialtyAr.trim(),
        specialtyEn: form.specialtyEn.trim(),

        experience: form.experience.trim(),

        bioAr: form.bioAr.trim(),
        bioEn: form.bioEn.trim(),

        image: form.image || "",

        createdAt: serverTimestamp(),
      });

      alert("تمت إضافة المدرب بنجاح ✅");

      resetForm();

      setShowForm(false);

      await fetchTrainers();
    } catch (error) {
      console.error("ADD TRAINER ERROR:", error);
      alert("حدث خطأ أثناء إضافة المدرب");
    } finally {
      setSaving(false);
    }
  };

  // =========================================================
  // DELETE TRAINER
  // =========================================================

  const handleDelete = async (id) => {
    const confirmed = window.confirm(
      "هل أنت متأكدة من حذف هذا المدرب؟"
    );

    if (!confirmed) return;

    try {
      await deleteDoc(doc(db, "trainers", id));

      setTrainers((prev) =>
        prev.filter((trainer) => trainer.id !== id)
      );

      alert("تم حذف المدرب بنجاح");
    } catch (error) {
      console.error("DELETE TRAINER ERROR:", error);
      alert("حدث خطأ أثناء الحذف");
    }
  };

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <div className="admin-trainers">

      {/* ================= HEADER ================= */}

      <div className="admin-trainers-header">

        <div>
          <span className="admin-label">
            ADMIN PANEL
          </span>

          <h1>المدربون</h1>

          <p>
            إدارة مدربي مركز مداد
          </p>
        </div>

        <button
          type="button"
          className="add-trainer-button"
          onClick={() => setShowForm((prev) => !prev)}
        >
          {showForm ? "إغلاق" : "+ إضافة مدرب"}
        </button>

      </div>

      {/* ================= FORM ================= */}

      {showForm && (
        <form
          className="trainer-form"
          onSubmit={handleSubmit}
        >

          <div className="form-title">
            <h2>إضافة مدرب جديد</h2>

            <p>
              أدخل معلومات المدرب التي ستظهر على الموقع.
            </p>
          </div>

          {/* ================= IMAGE ================= */}

          <div className="image-upload">

            <label className="main-label">
              صورة المدرب
            </label>

            <label
              className={`image-upload-box ${
                preview ? "has-image" : ""
              }`}
            >

              {preview ? (
                <>
                  <img
                    src={preview}
                    alt="Preview"
                    className="image-preview"
                  />

                  <div className="image-overlay">
                    <span>📷</span>
                    <p>اضغط لتغيير الصورة</p>
                  </div>
                </>
              ) : (
                <div className="upload-placeholder">

                  <span className="upload-icon">
                    📷
                  </span>

                  <p>
                    اضغط لاختيار صورة المدرب
                  </p>

                  <small>
                    JPG / PNG / WEBP
                  </small>

                </div>
              )}

              <input
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={handleImage}
                className="image-file-input"
              />

            </label>

            {preview && (
              <button
                type="button"
                className="remove-image-button"
                onClick={removeImage}
              >
                حذف الصورة
              </button>
            )}

          </div>

          {/* ================= NAMES ================= */}

          <div className="form-grid">

            <div className="form-group">

              <label>
                الاسم بالعربي *
              </label>

              <input
                type="text"
                name="nameAr"
                value={form.nameAr}
                onChange={handleChange}
                placeholder="مثال: محمد أحمد"
              />

            </div>

            <div className="form-group">

              <label>
                Name in English *
              </label>

              <input
                type="text"
                name="nameEn"
                value={form.nameEn}
                onChange={handleChange}
                placeholder="Example: Mohammad Ahmad"
              />

            </div>

          </div>

          {/* ================= SPECIALTY ================= */}

          <div className="form-grid">

            <div className="form-group">

              <label>
                الاختصاص بالعربي *
              </label>

              <input
                type="text"
                name="specialtyAr"
                value={form.specialtyAr}
                onChange={handleChange}
                placeholder="مثال: مدرب برمجة"
              />

            </div>

            <div className="form-group">

              <label>
                Specialty in English *
              </label>

              <input
                type="text"
                name="specialtyEn"
                value={form.specialtyEn}
                onChange={handleChange}
                placeholder="Example: Programming Trainer"
              />

            </div>

          </div>

          {/* ================= EXPERIENCE ================= */}

          <div className="form-group">

            <label>
              سنوات الخبرة
            </label>

            <input
              type="number"
              min="0"
              name="experience"
              value={form.experience}
              onChange={handleChange}
              placeholder="مثال: 5"
            />

          </div>

          {/* ================= BIO ================= */}

          <div className="form-grid">

            <div className="form-group">

              <label>
                نبذة بالعربي
              </label>

              <textarea
                name="bioAr"
                value={form.bioAr}
                onChange={handleChange}
                placeholder="اكتب نبذة قصيرة عن المدرب..."
                rows="5"
              />

            </div>

            <div className="form-group">

              <label>
                Biography in English
              </label>

              <textarea
                name="bioEn"
                value={form.bioEn}
                onChange={handleChange}
                placeholder="Write a short biography..."
                rows="5"
              />

            </div>

          </div>

          {/* ================= SUBMIT ================= */}

          <button
            type="submit"
            className="save-trainer"
            disabled={saving}
          >
            {saving
              ? "جاري الحفظ..."
              : "حفظ المدرب"}
          </button>

        </form>
      )}

      {/* ================= TRAINERS LIST ================= */}

      <div className="admin-trainers-list">

        <div className="list-header">

          <div>
            <h2>
              المدربون المضافون
            </h2>

            <p>
              جميع المدربين الموجودين في المركز
            </p>
          </div>

          <span className="trainers-count">
            {trainers.length} مدرب
          </span>

        </div>

        {/* LOADING */}

        {loading ? (
          <div className="admin-loading">
            جاري تحميل المدربين...
          </div>

        ) : trainers.length === 0 ? (

          <div className="admin-empty">

            <div className="empty-icon">
              👨‍🏫
            </div>

            <h3>
              لا يوجد مدربون بعد
            </h3>

            <p>
              ابدأ بإضافة أول مدرب إلى مركز مداد.
            </p>

          </div>

        ) : (

          <div className="admin-trainer-grid">

            {trainers.map((trainer) => (

              <div
                className="admin-trainer-card"
                key={trainer.id}
              >

                {/* IMAGE */}

                {trainer.image ? (
                  <img
                    src={trainer.image}
                    alt={trainer.nameAr}
                    className="trainer-card-image"
                  />
                ) : (
                  <div className="admin-no-image">
                    👨‍🏫
                  </div>
                )}

                {/* INFO */}

                <div className="admin-trainer-info">

                  <h3>
                    {trainer.nameAr}
                  </h3>

                  <p>
                    {trainer.specialtyAr}
                  </p>

                  {trainer.experience && (
                    <small>
                      {trainer.experience} سنوات خبرة
                    </small>
                  )}

                  <button
                    type="button"
                    className="delete-trainer"
                    onClick={() =>
                      handleDelete(trainer.id)
                    }
                  >
                    حذف المدرب
                  </button>

                </div>

              </div>

            ))}

          </div>

        )}

      </div>

    </div>
  );
}

export default AdminTrainers;